"use client"

import { ReportScope } from "@ibada/shared-types/reporting"

import type { AuthMeResponse } from "@/features/auth/model/auth-types"
import { resolveBranchIdForWorkspaceUserAccess } from "@/features/auth/lib/workspace-user-access"
import { resolveWorkspaceAccessRecord } from "@/features/dashboard/model/church-nav"

import type { ReportsRouteScope } from "@/features/reports/types/reports-types"

type BuildReportsRouteScopeInput = {
  authMe: AuthMeResponse | null | undefined
  workspaceSlug: string | null | undefined
  branchSlug: string | null | undefined
}

const PARISH_BRANCH_SLUG = "parish"

export function buildReportsRouteScope({
  authMe,
  workspaceSlug,
  branchSlug,
}: BuildReportsRouteScopeInput): ReportsRouteScope | null {
  if (!authMe || !workspaceSlug || !branchSlug) {
    return null
  }

  const accessRecord = resolveWorkspaceAccessRecord(authMe, workspaceSlug)
  const workspaceId = accessRecord?.workspace?.id ?? null
  const branchId = resolveBranchIdForWorkspaceUserAccess(accessRecord, branchSlug)

  if (!workspaceId || !branchId) {
    return null
  }

  const isParish = branchSlug.trim().toLowerCase() === PARISH_BRANCH_SLUG

  return {
    workspaceId,
    workspaceSlug,
    branchId,
    branchSlug,
    scopeKind: isParish ? ReportScope.PARISH : ReportScope.BRANCH,
    isParish,
  }
}

/**
 * Scope for a WORKSPACE-wide (consolidated) report run.
 *
 * `branchId` is deliberately empty: at workspace scope the engine applies NO branch predicate
 * (`buildImplicitBranchFilter` returns null for a null effective branch), and the API rejects a WORKSPACE scope
 * that carries `branchIds` at all. Carrying the active branch here would therefore be a value that must never
 * be sent -- an empty string states that plainly.
 *
 * ADR-0013 deferred branch-MULTI-select (picking N specific branches). Workspace scope is the all-branches
 * case, which the authorization service and planner already implement.
 */
export function buildWorkspaceReportsScope({
  authMe,
  workspaceSlug,
}: Omit<BuildReportsRouteScopeInput, "branchSlug">): ReportsRouteScope | null {
  if (!authMe || !workspaceSlug) {
    return null
  }

  const accessRecord = resolveWorkspaceAccessRecord(authMe, workspaceSlug)
  const workspaceId = accessRecord?.workspace?.id ?? null
  if (!workspaceId) {
    return null
  }

  return {
    workspaceId,
    workspaceSlug,
    branchId: "",
    branchSlug: "",
    scopeKind: ReportScope.WORKSPACE,
    isParish: false,
  }
}
